import {Dispatch} from 'redux'

import {DevicePlatform} from '@vio/core/src/_web/utils/userAgent/types'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  AnalyticsContext,
  BookingContext,
  Category,
  Entity,
  ErrorContext,
  Page,
  ServerErrorContext
} from '@vio/core/src/analytics/types/Events'
import {PaymentMethodType} from '@vio/core/src/api-types/bovio/request/booking_creation'
import {OfferPrice} from '@vio/core/src/offer/types/offer'
import {PaymentTypes} from '@vio/core/src/offer/types/paymentType'

interface TrackPaymentButtonClick {
  bookingRequestId: string
  merchantOfRecordCode: string
  paymentMethodType: PaymentMethodType
  dispatch: Dispatch
  priceTimer?: string | null
  paymentType?: PaymentTypes
  paymentMethodsEligible: PaymentMethodType[]
  paymentMethodPreselected: PaymentMethodType
  areSpecialRequestsAdded: boolean
  postalCode?: string
  bedPreferenceOptions?: string[]
  bedPreferenceValue?: string
  isPayInCardCurrencySelected: boolean
}

interface BaseTrackClick {
  dispatch: Dispatch
}

interface TrackPaymentPageDisplayed {
  offerCheckPrices: OfferPrice[]
  isSoldOut: boolean
  offerCheckUrl: string
  dispatch: Dispatch
  bookingContext?: BookingContext | null
  bedPreferenceOptions?: string[]
  /** Room name the guest sees, which differs from the offer's for roomless v2 */
  displayRoomName?: string
}

interface TrackPaymentPageErrored {
  serverErrorContext: ServerErrorContext | null
  dispatch: Dispatch
}

interface TrackBookingError {
  bookingRequestId: string
  merchantOfRecordCode: string
  errorContext: ErrorContext | null
  dispatch: Dispatch
  platform: DevicePlatform
  paymentMethod: string | undefined
}

interface TrackSplitBookingDetailsDisplayed {
  page: Page
  dispatch: Dispatch
}

interface TrackRoomDetailsClick extends BaseTrackClick {
  sbSelectedOfferPosition?: number
}

interface TrackBedPreferencePickerClick extends BaseTrackClick {
  bedPreferenceOption?: string
}

export const trackPaymentButtonClick = ({
  bookingRequestId,
  merchantOfRecordCode,
  paymentMethodType,
  paymentType,
  dispatch,
  priceTimer = null,
  paymentMethodsEligible,
  paymentMethodPreselected,
  areSpecialRequestsAdded,
  postalCode,
  bedPreferenceOptions,
  bedPreferenceValue,
  isPayInCardCurrencySelected
}: TrackPaymentButtonClick) => {
  dispatch(
    trackEvent({
      category: Category.User,
      entity: Entity.PaymentButton,
      action: Action.Clicked,
      page: Page.Payment,
      payload: {
        bookingRequestID: bookingRequestId,
        merchantOfRecord: merchantOfRecordCode,
        paymentMethod: paymentMethodType,
        ...(paymentType ? {chargeTypeSelected: paymentType} : {}),
        ...(priceTimer !== null ? {priceTimer} : {}),
        paymentMethodsEligible,
        paymentMethodPreselected,
        areSpecialRequestsAdded,
        isPostalCodeFilled: !!postalCode,
        bedPreferenceOptions,
        bedPreferenceValue,
        isPayInCardCurrencySelected
      }
    })
  )
}

export const trackHotelGalleryClick = ({dispatch}: BaseTrackClick) => {
  dispatch(
    trackEvent({
      category: Category.User,
      entity: Entity.HotelDetailsGallery,
      action: Action.Clicked,
      page: Page.Payment
    })
  )
}

export const trackCountrySelectorClick = ({dispatch}: BaseTrackClick) => {
  dispatch(
    trackEvent({
      category: Category.User,
      entity: Entity.Picker,
      action: Action.Clicked,
      page: Page.Payment,
      payload: {
        fieldName: 'cardCountry'
      }
    })
  )
}

export const trackFeesDetailsClick = ({dispatch}: BaseTrackClick) => {
  dispatch(
    trackEvent({
      category: Category.User,
      entity: Entity.FeesDetailsButton,
      action: Action.Clicked,
      page: Page.Payment
    })
  )
}

export const trackRoomDetailsClick = ({
  dispatch,
  sbSelectedOfferPosition
}: TrackRoomDetailsClick) => {
  if (sbSelectedOfferPosition === undefined) {
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.MoreDetailsButton,
        action: Action.Clicked,
        page: Page.Payment
      })
    )
    return
  }

  dispatch(
    trackEvent({
      category: Category.User,
      entity: Entity.RoomDetailsButton,
      action: Action.Clicked,
      page: Page.Payment,
      payload: {
        ...(sbSelectedOfferPosition ? {sbSelectedOfferPosition} : {})
      }
    })
  )
}

export const trackSplitBookingDetailsDisplayed = ({
  page,
  dispatch
}: TrackSplitBookingDetailsDisplayed) => {
  dispatch(
    trackEvent({
      category: Category.System,
      entity: Entity.SplitBookingDetails,
      action: Action.Displayed,
      page
    })
  )
}

export const trackPaymentPageDisplayed = ({
  isSoldOut,
  offerCheckUrl,
  dispatch,
  bookingContext,
  bedPreferenceOptions,
  displayRoomName
}: TrackPaymentPageDisplayed) => {
  const analyticsContext = {
    ...(bookingContext && {[AnalyticsContext.BookingContext]: bookingContext})
  }

  dispatch(
    trackEvent({
      category: Category.System,
      entity: Entity.PaymentPage,
      action: Action.Displayed,
      page: Page.Payment,
      payload: {
        soldOut: isSoldOut,
        preBookURL: offerCheckUrl,
        bedPreferenceOptions,
        displayRoomName
      },
      analyticsContext
    })
  )
}

export const trackPaymentPageErrored = ({
  serverErrorContext,
  dispatch
}: TrackPaymentPageErrored) => {
  const analyticsContext = {
    [AnalyticsContext.ServerErrorContext]: serverErrorContext
  }

  dispatch(
    trackEvent({
      category: Category.System,
      entity: Entity.PaymentPage,
      action: Action.Errored,
      page: Page.Payment,
      analyticsContext
    })
  )
}

export const trackBookingError = ({
  bookingRequestId,
  merchantOfRecordCode,
  errorContext,
  dispatch,
  platform,
  paymentMethod
}: TrackBookingError) => {
  const analyticsContext = {
    [AnalyticsContext.ErrorContext]: errorContext
  }

  dispatch(
    trackEvent({
      category: Category.System,
      entity: Entity.Booking,
      action: Action.Errored,
      page: Page.Payment,
      payload: {
        bookingRequestID: bookingRequestId,
        merchantOfRecord: merchantOfRecordCode,
        platform,
        paymentMethod
      },
      analyticsContext
    })
  )
}

export const trackHotelInformationLoaded = ({dispatch}: BaseTrackClick) => {
  dispatch(
    trackEvent({
      category: Category.System,
      entity: Entity.HotelInformation,
      action: Action.Loaded,
      page: Page.Payment
    })
  )
}

export const trackBedPreferencePickerClick = ({
  dispatch,
  bedPreferenceOption
}: TrackBedPreferencePickerClick) => {
  dispatch(
    trackEvent({
      category: Category.User,
      entity: Entity.Picker,
      action: Action.Clicked,
      page: Page.Payment,
      payload: {
        fieldName: 'bedPreference',
        selectedValue: bedPreferenceOption
      }
    })
  )
}
